import { useState, useMemo, useEffect, useRef } from "react";
import useAmplitude from "@/hooks/useAmplitude";
import {
  PDR_ANALYTICS_EVENTS,
  getBulkInputMethod,
  getParticipationCreatedAnalyticsData,
  getAddParticipationFlowStartedAnalyticsData,
  getParticipationFlowDurationSeconds,
  storePdrParticipationFlowContext,
  type PdrParticipationEntryPoint
} from "@/features/participation/_utils/amplitude";
import { Controller, FormProvider, useForm } from "react-hook-form";
import Papa from "papaparse";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import SegmentedButton from "@/components/SegmentedButton";
import Select from "@/components/Select";
import FileUpload from "@/components/FileUpload";
import TextField from "@/components/TextField";
import SingleDatePicker from "./SingleDatePicker";
import Banner from "@/components/Banner";
import { useMutation, useQuery } from "@apollo/client";
import { Person } from "@/types";
import PersonAutocomplete from "./PersonAutocomplete";
import { ActivityParticipationRole, AttendanceType, CreditRate } from "@/pages/activities/activity-types/types";
import { FINALIZE_PARTICIPATIONS, GET_STAGED_PARTICIPATIONS, STAGE_PARTICIPATIONS } from "../../../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  getDefaultParticipationLevel,
  getParticipationLevelOptions,
  getParticipationRoleOptions,
  isoToLocalDate,
  findSelectedCreditRate,
  shouldShowParticipationQuantity,
  validateParticipationQuantity,
  getParticipationQuantityLabel,
  formatDefaultParticipationQuantity
} from "../_utils";
import classNames from "classnames";
import { validateDate } from "../_utils";

export function parseCsvFirstColumn(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error(results.errors[0].message));
          return;
        }
        const pgaIds = (results.data as string[][])
          .map((row) => (row[0] ?? "").trim())
          .filter((val) => /^\d+$/.test(val))
          .map((id) => id.padStart(8, "0"));

        resolve(pgaIds);
      },
      error: (error) => reject(error)
    });
  });
}

const ADD_MODE_OPTIONS = [
  { label: "Single", value: "single" },
  { label: "Bulk", value: "bulk" }
];

export function parsePgaIds(text: string): string[] {
  return text
    .split(/[\n\s]+/)
    .map((id) => id.trim())
    .filter((val) => /^\d+$/.test(val))
    .map((id) => id.padStart(8, "0"));
}

export type AddParticipantsFormValues = {
  mode: "single" | "bulk";
  participationLevel: string;
  participationRole: string;
  startDate: string;
  endDate: string;
  pgaIdsText: string;
  person: string;
  selectedPgaId: string;
  quantity: string;
};

const getDefaultFormValues = (
  creditRates: CreditRate[],
  allowParticipationStarting?: string | null,
  activityDuration?: number | null
): AddParticipantsFormValues => {
  const today = new Date().toISOString().split("T")[0];
  const defaultDate = allowParticipationStarting?.split("T")[0]?.trim() || today;
  return {
    mode: "bulk",
    participationLevel: getDefaultParticipationLevel(creditRates),
    participationRole: ActivityParticipationRole.ATTENDEE,
    startDate: defaultDate,
    endDate: defaultDate,
    pgaIdsText: "",
    person: "",
    selectedPgaId: "",
    quantity: formatDefaultParticipationQuantity(activityDuration)
  };
};

export default function AddParticipantsModal({
  onDismiss,
  activityId,
  creditRates,
  activityDuration = null,
  allowParticipationStarting = "",
  allowParticipationThrough,
  onSuccess,
  stagedImportId: initialStagedImportId,
  finalizeOnSingleSave,
  onSingleSaveSuccess,
  entryPoint
}: {
  onDismiss: () => void;
  activityId: string;
  creditRates: CreditRate[];
  activityDuration?: number | null;
  allowParticipationStarting?: string | null;
  allowParticipationThrough?: string | null;
  onSuccess?: (processingId?: string) => void | Promise<unknown>;
  stagedImportId?: string | null;
  finalizeOnSingleSave?: boolean;
  onSingleSaveSuccess?: () => void | Promise<unknown>;
  entryPoint: PdrParticipationEntryPoint;
}) {
  const { Modal, close } = useModal();
  const { trackEvent } = useAmplitude();
  const flowStartedAtRef = useRef(Date.now());
  const [bannerError, setBannerError] = useState("");

  const defaultFormValues = useMemo(
    () => getDefaultFormValues(creditRates, allowParticipationStarting, activityDuration),
    [creditRates, allowParticipationStarting, activityDuration]
  );

  const form = useForm<AddParticipantsFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange"
  });

  const { control, watch, setValue, reset: resetForm } = form;

  const mode = watch("mode");
  const participationLevel = watch("participationLevel");
  const participationRole = watch("participationRole");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const pgaIdsText = watch("pgaIdsText");
  const selectedPgaId = watch("selectedPgaId");
  const quantity = watch("quantity");

  const selectedCreditRate = useMemo(
    () => findSelectedCreditRate(creditRates, participationLevel, participationRole),
    [creditRates, participationLevel, participationRole]
  );
  const showQuantityField =
    mode === "single" &&
    shouldShowParticipationQuantity(selectedCreditRate?.rateUnit, selectedCreditRate?.rateMultiplier);
  const parsedQuantity = parseFloat(quantity);
  const hasValidQuantity = !showQuantityField || validateParticipationQuantity(quantity) === true;

  const [stagedImportId, setStagedImportId] = useState<string | null>(initialStagedImportId ?? null);
  const isAddAnotherRef = useRef(false);
  const hasTrackedFlowStartRef = useRef(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvPgaIds, setCsvPgaIds] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [csvParsing, setCsvParsing] = useState(false);

  useEffect(() => {
    if (hasTrackedFlowStartRef.current) return;
    hasTrackedFlowStartRef.current = true;
    flowStartedAtRef.current = Date.now();
    trackEvent(
      PDR_ANALYTICS_EVENTS.addParticipationStarted,
      getAddParticipationFlowStartedAnalyticsData({
        source: entryPoint,
        activityId
      })
    );
  }, [activityId, entryPoint, trackEvent]);

  useEffect(() => {
    if (initialStagedImportId) setStagedImportId(initialStagedImportId);
  }, [initialStagedImportId]);

  useQuery<any>(GET_STAGED_PARTICIPATIONS, {
    variables: { stagedImportId: stagedImportId ?? "" },
    skip: !stagedImportId,
    fetchPolicy: "network-only",
    onError: (error) => {
      const isNotFound = error.graphQLErrors?.some((e) => e.message?.toLowerCase().includes("not found"));
      if (isNotFound) {
        setStagedImportId(null);
      }
    }
  });

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setValue("endDate", startDate);
    }
  }, [startDate, endDate, setValue]);

  useEffect(() => {
    if (mode === "single" && showQuantityField) {
      setValue("quantity", formatDefaultParticipationQuantity(activityDuration));
    }
  }, [activityDuration, mode, showQuantityField, setValue]);
  const [finalizeParticipations] = useMutation(FINALIZE_PARTICIPATIONS);
  const [stageParticipations, { loading }] = useMutation(STAGE_PARTICIPATIONS, {
    onCompleted: async (data) => {
      const result = data?.stageParticipations;
      if (result?.success) {
        const processingId = result.result.processingId;

        if (finalizeOnSingleSave && mode === "single" && !isAddAnotherRef.current && processingId) {
          try {
            const finalizeResult = await finalizeParticipations({
              variables: { input: { processingId } }
            });
            const finalizeData = finalizeResult.data?.finalizeParticipations;
            if (finalizeData?.success) {
              trackEvent(
                PDR_ANALYTICS_EVENTS.participationCreated,
                getParticipationCreatedAnalyticsData({
                  source: entryPoint,
                  mode: "single",
                  participationKind: "activity-level",
                  activityId,
                  participantCount: 1,
                  durationSeconds: getParticipationFlowDurationSeconds(flowStartedAtRef.current)
                })
              );
              showSnackbar(finalizeData.message ?? "Participant added", { close: true });
              close();
              onDismiss();
              await onSingleSaveSuccess?.();
            } else {
              setBannerError(finalizeData?.message ?? "Failed to add participant.");
            }
          } catch (e) {
            setBannerError((e as Error)?.message ?? "Failed to add participant.");
          }
          isAddAnotherRef.current = false;
          return;
        }

        if (mode === "bulk") {
          storePdrParticipationFlowContext(activityId, {
            startedAtMs: flowStartedAtRef.current,
            entryPoint,
            bulkInputMethod: getBulkInputMethod(!!selectedFile)
          });
        }

        if (processingId) setStagedImportId(processingId);
        await onSuccess?.(processingId ?? undefined);
        showSnackbar(`${result.result.totalCount ?? 0} participants added for review`, {
          close: true
        });
        if (!isAddAnotherRef.current) {
          close();
          onDismiss();
        }
        isAddAnotherRef.current = false;
      } else {
        setBannerError(result?.message ?? "Failed to stage participants.");
      }
    },
    onError: (error) => {
      isAddAnotherRef.current = false;
      setBannerError(error.message ?? "Failed to stage participants.");
    }
  });
  useEffect(() => {
    if (!selectedFile) {
      setCsvPgaIds([]);
      setUploadError("");
      setCsvParsing(false);
      return;
    }
    setUploadError("");
    setCsvParsing(true);
    parseCsvFirstColumn(selectedFile)
      .then((ids) => {
        setCsvPgaIds(ids);
        setCsvParsing(false);
      })
      .catch((err) => {
        setUploadError(err?.message ?? "Failed to parse CSV file");
        setCsvPgaIds([]);
        setCsvParsing(false);
      });
  }, [selectedFile]);

  const searchField = "person";
  const pgaIds = useMemo(
    () => (selectedFile ? csvPgaIds : parsePgaIds(pgaIdsText)),
    [selectedFile, csvPgaIds, pgaIdsText]
  );

  const totalParticipants = mode === "single" ? (selectedPgaId ? 1 : 0) : pgaIds.length;
  const canContinue =
    participationLevel &&
    !uploadError &&
    !csvParsing &&
    hasValidQuantity &&
    (mode === "single" ? !!selectedPgaId : pgaIds.length > 0);

  const handleFileUpload = (file: File) => {
    setSelectedFile(file);
    setUploadError("");
    setValue("pgaIdsText", "");
  };

  const getPgaIdsToSubmit = async (): Promise<string[]> => {
    if (mode === "single") {
      return selectedPgaId ? [selectedPgaId] : [];
    }
    if (selectedFile) {
      return csvPgaIds;
    }
    return pgaIds;
  };

  const buildParticipationsInput = (pgaIds: string[]) =>
    pgaIds.map((pgaId) => ({
      pgaId,
      activityId,
      attendanceType: participationLevel,
      participationRole: participationRole || ActivityParticipationRole.ATTENDEE,
      startDate,
      endDate,
      ...(showQuantityField ? { quantity: parsedQuantity } : {})
    }));

  const handleContinue = async () => {
    if (!canContinue) return;
    setBannerError("");
    const pgaIds = await getPgaIdsToSubmit();
    if (pgaIds.length === 0) return;

    await stageParticipations({
      variables: {
        input: {
          participations: buildParticipationsInput(pgaIds),
          stagedImportId: stagedImportId ?? undefined
        }
      }
    });
  };

  const handleSave = async () => {
    if (!canContinue) return;
    setBannerError("");
    const pgaIds = await getPgaIdsToSubmit();
    if (pgaIds.length === 0) return;

    await stageParticipations({
      variables: {
        input: {
          participations: buildParticipationsInput(pgaIds),
          stagedImportId: finalizeOnSingleSave ? undefined : (stagedImportId ?? undefined)
        }
      }
    });
  };

  const handleAddAnother = async () => {
    if (!canContinue) return;
    setBannerError("");
    if (!selectedPgaId) return;
    isAddAnotherRef.current = true;
    await stageParticipations({
      variables: {
        input: {
          participations: buildParticipationsInput([selectedPgaId]),
          stagedImportId: stagedImportId ?? undefined
        }
      }
    });
    setValue("selectedPgaId", "");
    setValue("person", "");
    setValue("quantity", formatDefaultParticipationQuantity(activityDuration));
  };

  const selectPerson = (name: string, value: Person, setValueFn: (name: string, value: string) => void) => {
    setValue("selectedPgaId", value.pgaId || "");
    setValueFn(name, `${value.firstName} ${value.lastName}`);
  };

  const handleResetForm = () => {
    setBannerError("");
    setStagedImportId(null);
    setSelectedFile(null);
    setUploadError("");
    setFileUploadKey((k) => k + 1);
    resetForm(getDefaultFormValues(creditRates, allowParticipationStarting, activityDuration));
  };
  return (
    <Modal
      onClosed={() => {
        handleResetForm();
        onDismiss();
      }}
      actions={
        mode === "single" ? (
          <div className="flex flex-1 justify-between w-full gap-2">
            <Button
              variant="text"
              onClick={handleAddAnother}
              disabled={!canContinue || loading}
              data-testid="add-participants-add-another-button"
            >
              Save and Add Another
            </Button>
            <div className="flex gap-2">
              <Button variant="text" onClick={close} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="filled"
                onClick={handleSave}
                disabled={!canContinue || loading}
                data-testid="add-participants-save-button"
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Button variant="text" onClick={close} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleContinue}
              disabled={!canContinue || loading}
              icon={loading || csvParsing ? "progress_activity" : undefined}
              iconClassName={classNames((loading || csvParsing) && "animate-spin w-6 h-6 !block")}
              data-testid="add-participants-continue-button"
            >
              {loading ? "Uploading..." : csvParsing ? "Parsing..." : "Continue"}
            </Button>
          </>
        )
      }
      headline="Add Participant(s)"
      hasFormElements
      className="h-fit max-h-[90vh] md:w-[490px]"
    >
      <FormProvider {...form}>
        <div className="space-y-6">
          {!!bannerError && (
            <Banner inline variant="error" description={bannerError} closable onClose={() => setBannerError("")} />
          )}

          <Controller
            name="mode"
            control={control}
            render={({ field: { onChange, value } }) => (
              <SegmentedButton
                options={ADD_MODE_OPTIONS}
                value={value}
                onChange={(v) => onChange((v as "single" | "bulk") ?? "bulk")}
                maxButtonWidth={200}
                showCheckmark
              />
            )}
          />
          {getParticipationLevelOptions(creditRates).length > 1 && (
            <Controller
              name="participationLevel"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Attendance Type"
                  required
                  options={getParticipationLevelOptions(creditRates)}
                  value={value}
                  onSelect={(v) => onChange(v as AttendanceType)}
                />
              )}
            />
          )}
          {getParticipationRoleOptions(creditRates).length > 1 && (
            <Controller
              name="participationRole"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Participation Role"
                  required
                  options={getParticipationRoleOptions(creditRates)}
                  value={value}
                  onSelect={(v) => onChange(v as ActivityParticipationRole)}
                />
              )}
            />
          )}
          <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
            <SingleDatePicker<AddParticipantsFormValues>
              control={control}
              name="startDate"
              label="Start Date"
              required
              useModal
              minDate={isoToLocalDate(allowParticipationStarting)}
              maxDate={isoToLocalDate(allowParticipationThrough, true)}
              rules={{
                validate: (value) =>
                  validateDate(value, "Start Date", allowParticipationStarting, allowParticipationThrough)
              }}
            />
            <SingleDatePicker<AddParticipantsFormValues>
              control={control}
              name="endDate"
              label="End Date"
              required
              useModal
              minDate={isoToLocalDate(startDate)}
              maxDate={isoToLocalDate(allowParticipationThrough, true)}
              rules={{
                validate: (value) =>
                  validateDate(value, "End Date", allowParticipationStarting, allowParticipationThrough)
              }}
            />
          </div>
          {mode === "single" && showQuantityField && (
            <Controller
              name="quantity"
              control={control}
              rules={{
                required: "Quantity is required",
                validate: validateParticipationQuantity
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label={getParticipationQuantityLabel(selectedCreditRate?.rateUnit)}
                  value={value}
                  onChangeText={onChange}
                  required
                  variant="outlined"
                  className="w-full"
                  type="number"
                  error={!!error}
                  errorText={error?.message}
                  data-testid="add-participants-quantity-field"
                />
              )}
            />
          )}
          {mode === "single" ? (
            <Controller
              name={searchField}
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <PersonAutocomplete
                  label="Person"
                  required
                  query={value}
                  onChangeText={onChange}
                  reset={() => {
                    onChange("");
                    setValue("selectedPgaId", "");
                  }}
                  onSelect={(person) =>
                    selectPerson(name, person, (n, v) => setValue(n as keyof AddParticipantsFormValues, v))
                  }
                />
              )}
            />
          ) : (
            <>
              <div>
                <h3 className="text-title-medium text-sys-brand-on-surface mb-1">Upload Participants</h3>
                <p className="text-body-medium text-sys-brand-on-surface-variant mb-3">
                  Participants can be uploaded via a csv file or by copying/pasting a list of PGA IDs. The first column
                  of the csv <strong>must</strong> be PGA IDs only.
                </p>

                <FileUpload
                  key={fileUploadKey}
                  label="Choose a File to Upload"
                  subtitle="Allowed file extensions: csv"
                  buttonText="Upload"
                  handleUpload={handleFileUpload}
                  accept=".csv"
                  errorMsg={uploadError}
                  onError={setUploadError}
                  onClear={() => {
                    setSelectedFile(null);
                    setCsvPgaIds([]);
                    setUploadError("");
                    setFileUploadKey((k) => k + 1);
                  }}
                />

                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-sys-brand-outline" />
                  <span className="text-body-medium text-sys-brand-on-surface-variant">or</span>
                  <div className="flex-1 h-px bg-sys-brand-outline" />
                </div>

                <Controller
                  name="pgaIdsText"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Participant PGA IDs"
                      value={value}
                      onChangeText={(val) => {
                        onChange(val);
                        setSelectedFile(null);
                        setUploadError("");
                        setFileUploadKey((k) => k + 1);
                      }}
                      type="textarea"
                      rows={6}
                      variant="outlined"
                      className="w-full"
                    />
                  )}
                />
                <p className="text-body-large  text-sys-brand-on-surface-variant mt-6 mb-4">
                  <span className="font-bold"> Total Participants to Add: </span>
                  {totalParticipants}
                </p>
              </div>
            </>
          )}
        </div>
      </FormProvider>
    </Modal>
  );
}
